import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ProfileEditForm } from '../components/profile/ProfileEditForm';
import { WebhookSettings } from '../components/profile/WebhookSettings';
import { Loader } from '../components/ui/Loader';
import { useGetUserProfileQuery } from '../store/api/userApi';

export function ProfilePage() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useGetUserProfileQuery();
  const [isEditing, setIsEditing] = useState(false);

  const handleEditSuccess = () => {
    setIsEditing(false);
    void refetch();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('profile.error.title')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } }) 
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/dashboard"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('profile.error.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const user = data.data.user;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('profile.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('profile.subtitle')}
            </p>
          </div>

          {/* Profile Card */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            {!isEditing ? (
              <>
                {/* View Mode */}
                <div className="space-y-6">
                  {/* User Info Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                        {t('profile.info.firstName')}
                      </label>
                      <p className="text-lg text-neutral-light font-body">
                        {user.firstName}
                      </p>
                    </div>
                    {user.lastName && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                          {t('profile.info.lastName')}
                        </label>
                        <p className="text-lg text-neutral-light font-body">
                          {user.lastName}
                        </p>
                      </div>
                    )}
                    {user.email && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                          {t('profile.info.email')}
                        </label>
                        <p className="text-lg text-neutral-light font-body">
                          {user.email}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                        {t('profile.info.language')}
                      </label>
                      <p className="text-lg text-neutral-light font-body">
                        {user.language === 'ar' ? t('profile.edit.languageAr') : t('profile.edit.languageEn')}
                      </p>
                    </div>
                    {user.telegramId && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                          {t('profile.info.telegramId')}
                        </label>
                        <p className="text-lg text-neutral-light font-body">
                          {user.telegramId}
                        </p>
                      </div>
                    )}
                    {user.username && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                          {t('profile.info.username')}
                        </label>
                        <p className="text-lg text-neutral-light font-body">
                          @{user.username}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Balance Section */}
                  <div className="border-t border-neutral-dark pt-6">
                    <h3 className="text-xl font-heading font-semibold text-brand-accent mb-4">
                      {t('profile.balance.title')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-neutral-dark/30 rounded-lg p-4">
                        <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                          {t('profile.balance.main')}
                        </label>
                        <p className="text-2xl font-bold text-brand-accent font-heading">
                          {user.balance.toFixed(2)} USD
                        </p>
                      </div>
                      <div className="bg-neutral-dark/30 rounded-lg p-4">
                        <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                          {t('profile.balance.referral')}
                        </label>
                        <p className="text-2xl font-bold text-brand-accent font-heading">
                          {user.referralBalance.toFixed(2)} USD
                        </p>
                      </div>
                      <div className="bg-neutral-dark/30 rounded-lg p-4">
                        <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                          {t('profile.balance.cashback')}
                        </label>
                        <p className="text-2xl font-bold text-brand-accent font-heading">
                          {user.cashbackBalance.toFixed(2)} USD
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Statistics Section */}
                  <div className="border-t border-neutral-dark pt-6">
                    <h3 className="text-xl font-heading font-semibold text-brand-accent mb-4">
                      {t('profile.stats.title')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {typeof user.referralCount === 'number' && (
                        <div className="bg-neutral-dark/30 rounded-lg p-4">
                          <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                            {t('profile.stats.referrals')}
                          </label>
                          <p className="text-2xl font-bold text-brand-accent font-heading">
                            {user.referralCount}
                          </p>
                        </div>
                      )}
                      {typeof user.apiKeysCount === 'number' && (
                        <div className="bg-neutral-dark/30 rounded-lg p-4">
                          <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                            {t('profile.stats.apiKeys')}
                          </label>
                          <p className="text-2xl font-bold text-brand-accent font-heading">
                            {user.apiKeysCount}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="border-t border-neutral-dark pt-6 flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                    >
                      {t('profile.actions.edit')}
                    </button>
                    <Link
                      to="/balance"
                      className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 text-center"
                    >
                      {t('profile.actions.recharge')}
                    </Link>
                    <Link
                      to="/api-keys"
                      className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 text-center"
                    >
                      {t('profile.actions.apiKeys')}
                    </Link>
                    <Link
                      to="/dashboard"
                      className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 text-center"
                    >
                      {t('profile.actions.back')}
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Edit Mode */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-heading font-bold text-brand-accent">
                      {t('profile.edit.title')}
                    </h2>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="text-neutral-medium hover:text-neutral-light transition-colors font-body"
                    >
                      {t('common.close')}
                    </button>
                  </div>
                  <ProfileEditForm user={user} onSuccess={handleEditSuccess} />
                </div>
              </>
            )}
          </div>

          {/* Webhook Settings Section */}
          <div className="mt-6">
            <WebhookSettings />
          </div>
        </div>
      </div>
    </div>
  );
}

